import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/components/auth/AuthProvider';
import { GearCategory, CAMERA_CATEGORIES, GE_CATEGORIES } from '@/types/gear';
import { Label } from '@/components/ui/label';
import { useTranslation } from 'react-i18next';

interface QuickAddInventoryItemFormProps {
  onClose: () => void;
  onSubmit: (data: any) => void;
}

export function QuickAddInventoryItemForm({ onClose, onSubmit }: QuickAddInventoryItemFormProps) {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [category, setCategory] = useState<GearCategory>('Other');
  const [costPerUnit, setCostPerUnit] = useState('0');
  const [quantity, setQuantity] = useState('1');
  const [department, setDepartment] = useState<'camera' | 'ge'>('camera');
  const { t } = useTranslation();
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      return;
    }
    
    if (!name.trim()) {
      return;
    }
    
    const itemData = {
      name: name.trim(),
      category,
      cost_per_unit: parseFloat(costPerUnit) || 0,
      quantity: parseInt(quantity) || 1,
      user_id: user.id,
      department
    };
    
    onSubmit(itemData);
  };
  
  // Get the appropriate categories based on selected department
  const departmentCategories = department === 'camera' ? CAMERA_CATEGORIES : GE_CATEGORIES;
  
  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="space-y-2">
        <Input 
          placeholder={t('item_name_placeholder')} 
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoFocus
        />
      </div>
      
      <div className="space-y-2">
        <Select value={department} onValueChange={(value) => setDepartment(value as 'camera' | 'ge')}>
          <SelectTrigger>
            <SelectValue placeholder={t('select_department')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="camera">{t('camera_department')}</SelectItem>
            <SelectItem value="ge">{t('ge_department')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="space-y-2">
        <Select value={category} onValueChange={(value) => setCategory(value as GearCategory)}>
          <SelectTrigger>
            <SelectValue placeholder={t('select_category')} />
          </SelectTrigger>
          <SelectContent>
            {departmentCategories.map((cat) => (
              <SelectItem key={cat} value={cat}>
                {cat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">{t('cost_per_day_label_short')}</Label>
          <Input 
            type="number"
            placeholder={t('cost_per_day_placeholder')} 
            value={costPerUnit}
            onChange={(e) => setCostPerUnit(e.target.value)}
            min="0"
            step="0.01"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">{t('total_quantity_label')}</Label>
          <Input 
            type="number"
            placeholder={t('quantity_placeholder')} 
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            min="1"
          />
        </div>
      </div>
      
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button type="submit" disabled={!name.trim()}>
          {t('add_item')}
        </Button>
      </div>
    </form>
  );
}
